import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { APIService } from 'src/app/services/api.service';
import { NotifierService } from 'src/app/services/notifier.service';
import { getENV } from '../../../../../../../_helpers/helpers';

interface EQTrack {
    id: number;
    RaceLength: number;  // Will store decimal values
    EQTrackName: string;
    TrackType: string;
    AdditionalStart: string;
    TrackLength: string;
    TrackID: string;
}

@Component({
  selector: 'app-eq-track-management',
  templateUrl: './eq-track-management.component.html',
  styleUrls: ['./eq-track-management.component.css']
})
export class EQTrackManagementComponent implements OnInit {
  tracks: EQTrack[] = [];
  venueId: string;
  loading = false;
  editMode = false;
  config: any;

  constructor(
    private route: ActivatedRoute,
    private apiService: APIService,
    private notifier: NotifierService
  ) {
    this.config = this.route.snapshot.data['config'];
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.venueId = params['venue_id'];
      this.loadTracks();
    });
  }

  async loadTracks(): Promise<void> {
    try {
      this.loading = true;
      this.notifier.loading(true);

      const apiURL = `${this.config[getENV()].raceAPI}/geteqtrack?ExternalVenueId=${this.venueId}`;
      const result = await this.apiService.getDataPromis(apiURL, {}, {});

      if (result.venue && result.venue.TrackList) {
        this.tracks = result.venue.TrackList.map((track: any) => ({
          id: track.id,
          RaceLength: parseFloat(track.RaceLength) || 0, // Parse as float to preserve decimals
          EQTrackName: track.EQTrackName,
          TrackType: track.TrackType,
          AdditionalStart: track.AdditionalStart,
          TrackLength: track.TrackLength,
          TrackID: track.TrackID
        })).sort((a, b) => a.RaceLength - b.RaceLength);
      }
    } catch (error) {
      this.notifier.alert('Error', '', 'Failed to load tracks', 'error', 5000);
      console.error('Error loading tracks:', error);
    } finally {
      this.loading = false;
      this.notifier.loading(false);
    }
  }

  toggleEditMode(): void {
    this.editMode = !this.editMode;
  }

  addNewTrack(): void {
    const newTrack: EQTrack = {
      id: 0, // Will be assigned by backend
      RaceLength: 0.0, // Initialize with decimal
      EQTrackName: '',
      TrackType: '',
      AdditionalStart: '0',
      TrackLength: '0',
      TrackID:''
    };
    this.tracks.push(newTrack);
    this.editMode = true;
  }

  removeTrack(index: number): void {
    this.tracks.splice(index, 1);
  }

  async saveChanges(): Promise<void> {
    try {
      this.loading = true;
      this.notifier.loading(true);

      // Validate tracks before saving
      const invalidTracks = this.tracks.filter(track =>
        !track.EQTrackName ||
        !track.RaceLength ||
        !track.TrackType
      );

      if (invalidTracks.length > 0) {
        this.notifier.alert('Error', '', 'Please fill all required fields', 'error', 5000);
        return;
      }

      const apiURL = `${this.config[getENV()].raceAPI}/updateeqtrack`;

      // Format tracks array to match expected format and ensure RaceLength is a string
      const formattedTracks = this.tracks.map(track => ({
        id: track.id,
        RaceLength: track.RaceLength.toFixed(1), // Ensure one decimal place
        EQTrackName: track.EQTrackName,
        TrackType: track.TrackType,
        AdditionalStart: track.AdditionalStart,
        TrackLength: track.TrackLength,
        TrackID: track.TrackID
      }));

      // Add query parameters to URL
      const urlWithParams = `${apiURL}?ExternalVenueId=${this.venueId}`;

      // Send only the tracks array as the body
      const result = await this.apiService.postDataPromis(urlWithParams, formattedTracks, {});

      if (result.message='Track details updated successfully.') {
        this.notifier.alert('Info', '', 'Tracks updated successfully', 'info', 5000);
        this.editMode = false;
        await this.loadTracks(); // Reload tracks to get fresh data
      } else {
        this.notifier.alert('Error', '', result.message || 'Failed to update tracks', 'error', 5000);
      }
    } catch (error) {
      this.notifier.alert('Error', '', 'Failed to save changes', 'error', 5000);
      console.error('Error saving tracks:', error);
    } finally {
      this.loading = false;
      this.notifier.loading(false);
    }
  }

  cancelEdit(): void {
    this.editMode = false;
    this.loadTracks(); // Reload original data
  }
}
